import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Mail } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

// TikTok icon (lucide-react has no built-in TikTok icon)
const TikTokIcon = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.95a8.16 8.16 0 0 0 4.77 1.52V7.05a4.85 4.85 0 0 1-1.84-.36z" />
  </svg>
);

const Footer = () => {
  const { t } = useLanguage();
  const year = new Date().getFullYear();

  return (
    <footer data-testid="main-footer" className="bg-[#0A0A0A] border-t border-white/10 mt-0">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="font-brush text-5xl text-[#00A651] tracking-wider leading-none">
            4KEEPERZ
          </div>
          <p className="text-white/60 text-sm mt-3 max-w-xs leading-relaxed">{t.footer.tagline}</p>
          <div className="flex items-center gap-4 mt-6">
            <a
              href="https://www.instagram.com/4keeperz.ch/"
              target="_blank"
              rel="noreferrer"
              className="w-10 h-10 border border-white/15 flex items-center justify-center hover:border-[#00A651] hover:text-[#00A651] transition-colors"
              data-testid="footer-instagram"
              aria-label="Instagram"
            >
              <Instagram className="w-4 h-4" />
            </a>
            <a
              href="https://www.tiktok.com/@4keeperz"
              target="_blank"
              rel="noreferrer"
              className="w-10 h-10 border border-white/15 flex items-center justify-center hover:border-[#00A651] hover:text-[#00A651] transition-colors"
              data-testid="footer-tiktok"
              aria-label="TikTok"
            >
              <TikTokIcon className="w-4 h-4" />
            </a>
            <a
              href="mailto:info@4keeperz.ch"
              className="w-10 h-10 border border-white/15 flex items-center justify-center hover:border-[#00A651] hover:text-[#00A651] transition-colors"
              data-testid="footer-email"
              aria-label="Email"
            >
              <Mail className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div>
          <h4 className="font-condensed uppercase tracking-[0.2em] text-xs text-white/50 mb-4">
            {t.footer_legal.title}
          </h4>
          <ul className="space-y-3 text-sm">
            <li>
              <Link to="/blog" className="text-white/70 hover:text-[#00A651] transition-colors" data-testid="footer-blog">
                {t.footer_legal.blog}
              </Link>
            </li>
            <li>
              <Link to="/torwarthandschuhe-damen" className="text-white/70 hover:text-[#00A651] transition-colors" data-testid="footer-damen">
                Torwarthandschuhe Damen
              </Link>
            </li>
            <li>
              <Link to="/legal/impressum" className="text-white/70 hover:text-[#00A651] transition-colors" data-testid="footer-imprint">
                {t.footer_legal.imprint}
              </Link>
            </li>
            <li>
              <Link to="/legal/datenschutz" className="text-white/70 hover:text-[#00A651] transition-colors" data-testid="footer-privacy">
                {t.footer_legal.privacy}
              </Link>
            </li>
            <li>
              <Link to="/legal/agb" className="text-white/70 hover:text-[#00A651] transition-colors" data-testid="footer-terms">
                {t.footer_legal.terms}
              </Link>
            </li>
            <li>
              <Link to="/legal/versand" className="text-white/70 hover:text-[#00A651] transition-colors" data-testid="footer-shipping">
                {t.footer_legal.shipping}
              </Link>
            </li>
          </ul>
        </div>

        <div className="text-sm text-white/60 space-y-1">
          <h4 className="font-condensed uppercase tracking-[0.2em] text-xs text-white/50 mb-4">
            Contact
          </h4>
          <div>Eschenbach, Switzerland</div>
          <a
            href="mailto:info@4keeperz.ch"
            className="hover:text-[#00A651] transition-colors block"
            data-testid="footer-contact-email"
          >
            info@4keeperz.ch
          </a>
        </div>
      </div>

      <div className="border-t border-white/5 py-6">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 flex flex-col md:flex-row justify-between items-center gap-2 text-xs text-white/40">
          <div>© {year} 4keeperz · {t.footer.rights}</div>
          <div className="font-condensed uppercase tracking-widest">Built with passion in Eschenbach</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
